import { useEffect, useState } from 'react';
import Dialog from '../../../organisms/Dialog';
import Loading from '../../../ions/Loading';
import { recoveryApi } from '../../../../services/recoveryApi';
import type { LegalCaseListItem } from '../../../../types/LegalCase';
import { creditorCellFilters, toCreditorCaseRows, type CreditorCell, type CreditorCaseRow } from './creditorCases';

// Visão geral dos casos de uma célula do relatório CPC 25 (AF-3817): credor × polo × classificação,
// sem sair do relatório. Lê o page-list com o mesmo recorte do drill-down; "Abrir na grid" leva
// para a grid de Casos com esses filtros.
interface CreditorCasesModalProps {
    cell: CreditorCell;
    title: string;
    onClose: () => void;
    onOpenCase: (id: string) => void;
    onOpenGrid: () => void;
}

const MODAL_PAGE_SIZE = 100;

const CreditorCasesModal = ({ cell, title, onClose, onOpenCase, onOpenGrid }: CreditorCasesModalProps) => {
    const [rows, setRows] = useState<CreditorCaseRow[] | null>(null);
    const [total, setTotal] = useState(0);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        recoveryApi.getLegalCases({ ...creditorCellFilters(cell), size: MODAL_PAGE_SIZE })
            .then((page: { items: LegalCaseListItem[]; total: number }) => {
                if (!active) return;
                setRows(toCreditorCaseRows(page.items || []));
                setTotal(page.total || 0);
            })
            .catch(() => { if (active) setFailed(true); });
        return () => { active = false; };
    }, [cell]);

    const body = () => {
        if (failed) return <p className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar os casos. Tente de novo.</p>;
        if (!rows) return <Loading />;
        if (rows.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum caso nesta célula.</p>;
        return (
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs text-gray-500 dark:text-gray-400 text-left">
                        <th className="py-2 px-2 font-medium">Processo</th>
                        <th className="py-2 px-2 font-medium">Devedor</th>
                        <th className="py-2 px-2 font-medium">Operação / CPR-F</th>
                        <th className="py-2 px-2 font-medium">Classificação</th>
                        <th className="py-2 px-2 font-medium text-right">Valor da causa</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id} className="border-t border-gray-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700">
                            <td className="py-2 px-2">
                                <button type="button" onClick={() => onOpenCase(row.id)}
                                    className="font-mono text-xs text-blue-700 dark:text-blue-400 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                                    {row.externalCaseNumber}
                                </button>
                            </td>
                            <td className="py-2 px-2 dark:text-white">{row.debtor}</td>
                            <td className="py-2 px-2 dark:text-gray-300">{row.operations}</td>
                            <td className="py-2 px-2 dark:text-gray-300">{row.classification}</td>
                            <td className="py-2 px-2 text-right dark:text-gray-200">{row.claimValue}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <Dialog
            id="creditor-cases-modal"
            show={true}
            onClose={onClose}
            header={title}
            size="lg"
            body={<div className="px-6 py-5 space-y-3">{body()}{total > MODAL_PAGE_SIZE && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Mostrando {MODAL_PAGE_SIZE} de {total}. Abra na grid para ver todos.</p>
            )}</div>}
            footer={
                <div className="flex justify-end gap-2 px-6 py-3">
                    <button type="button" onClick={onOpenGrid}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium">
                        Abrir na grid de Casos
                    </button>
                </div>
            }
        />
    );
};

export default CreditorCasesModal;
